import React, { useEffect, useState } from "react";

// region External libraries
import { ManageAccounts } from "@mui/icons-material";
import { Divider, Tooltip } from "@mui/material";
import { atom } from "@libraries/jotai";
// endregion External libraries
// region Languages
import useTranslation from "@languages/useTranslation";
import { GlobalMessages, UserRightsMessages } from "@languages/interfaces";
// endregion Languages
// region Atoms
import UserIcon from "@atoms/UserIcon";
import UserDetails from "@atoms/UserDetails";
import UserModuleList from "src/atoms/UserModuleList";
// endregion Atoms
// region Hooks
import { useIdentity, PERMISSIONS } from "@hooks/useIdentity";
// endregion Hooks
// region Molecules
// endregion Molecules
// region Organisms
import { PageTabProps, useTabsAtom } from "@organisms/PageTabs";
// endregion Organisms
// region Pages
import User from "@pages/User";
import { userRightsTabsAtom, selectedTabAtom } from "@pages/UsersRights";
// endregion Pages
// region Store
import { UserType } from "src/store/graphql/helpers/types.graphql";
// endregion Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

interface IUserProfileProps {
  currentUser: UserType;
  disableMoreInfo?: boolean;
}

const UserProfile : React.FC<IUserProfileProps> = ({ currentUser, disableMoreInfo }) => {
  // region states
  const [userAnchorElement, setUserAnchorElement] = useState<HTMLElement | null>(null);
  // endregion states

  // region hooks
  const { t } = useTranslation();
  const { setSelectedTab, addNewTab } = useTabsAtom(userRightsTabsAtom, selectedTabAtom);
  const { validateUserAccess } = useIdentity();
  const [hasUserAlterPermission, setHasUserAlterPermission] = useState(false);
  // endregion hooks

  // region constants
  const openMoreInfo = !!userAnchorElement;
  // endregion constants

  // region functions
  const closeMoreInfoPopover = () => setUserAnchorElement(null);

  /**
   * Returns the names of the modules this user is allowed to enter. Prefers
   * the canonical source (UserModuleAccess via the userModuleAccesses field);
   * falls back to deriving from userRoles → role.module for backwards
   * compatibility while the frontend codegen catches up with the backend
   * change in production. See docs/MODULE_ACCESS.md.
   */
  const getModuleNamesOfUserRoles = () => {
    const fromAccesses = currentUser.userModuleAccesses
      ?.map((access) => access?.module?.name)
      .filter((name): name is string => Boolean(name));

    if (fromAccesses && fromAccesses.length > 0) return fromAccesses;

    return currentUser.userRoles?.map((userRole) => userRole?.role?.module?.name || "") || [];
  };
  // endregion functions

  // region handlers
  const handleClickMoreInfo = (event: React.MouseEvent<HTMLDivElement>) => {
    setUserAnchorElement(event.currentTarget);
  };

  const handleClickEditUser = () => {
    if (!hasUserAlterPermission) return;

    const newSelectedTabAtom = atom<number>(0);
    const newTab: PageTabProps = {
      id: currentUser.id,
      tab: {
        title: UserRightsMessages.userControl,
        caption: UserRightsMessages.editUserTabCaption
      },
      tabPanel: <User selectedStepTabAtom={newSelectedTabAtom} mode="EDIT" currentUserId={currentUser.id} />
    };
    const indexNewTab = addNewTab(newTab);

    closeMoreInfoPopover();
    setSelectedTab(indexNewTab);
  };
  // endregion handlers

  // region effects
  useEffect(() => {
    setHasUserAlterPermission(validateUserAccess(PERMISSIONS.USER_RIGHTS_CONTROL.USERS.ALTER));
  }, [validateUserAccess]);
  // endregion effects

  return (
    <Styled.Container>
      <UserIcon
        userImg={currentUser.imageUrl || undefined}
        onClick={handleClickMoreInfo}
        blockMoreInfo={disableMoreInfo}
        active={currentUser.active}
      />
      <Tooltip title={currentUser.name} tabIndex={-1}>
        <Styled.UserName>{currentUser.name}</Styled.UserName>
      </Tooltip>
      <Styled.MoreInfoPopover
        open={openMoreInfo}
        anchorEl={userAnchorElement}
        onClose={closeMoreInfoPopover}
        anchorOrigin={{
          vertical: -12,
          horizontal: -10
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
      >
        <Styled.UserInfo>
          <UserDetails
            name={currentUser.name}
            email={currentUser.email}
            position={currentUser.position}
          />
        </Styled.UserInfo>
        <UserModuleList modules={getModuleNamesOfUserRoles()} />
        <Divider />
        <Styled.EditUser>
          <Styled.EditUserButton
            onClick={handleClickEditUser}
            className={!hasUserAlterPermission ? "no-permission" : ""}
          >
            <ManageAccounts />
            {t(GlobalMessages.editUser)}
          </Styled.EditUserButton>
        </Styled.EditUser>
      </Styled.MoreInfoPopover>
    </Styled.Container>
  );
};

UserProfile.defaultProps = {
  disableMoreInfo: false
};

export default UserProfile;